import React from "react";
import { toast, Slide } from "react-toastify";
import strings from "translate/language";
import { resetGlobalLoader } from "./globalLoader";
import "./toastNotify.css";

const TOAST_ICONS = {
  success: "fa-check",
  error: "fa-times",
  warning: "fa-exclamation",
  info: "fa-info",
};

const TOAST_TITLES = {
  success: "Success",
  error: "Something went wrong",
  warning: "Please check",
  info: "Update",
};

const GENERIC_TITLES = new Set([
  "Success",
  "Failed",
  "Error",
  "Warning",
  "Notice",
  "Something went wrong",
  "Please check",
  "Update",
]);

const TOAST_DEDUPE_MS = 900;
const DEFAULT_AUTO_CLOSE_MS = 4000;

let lastToastSignature = "";
let lastToastTimestamp = 0;

function buildToastSignature(type, message, title) {
  return `${type}::${title}::${message}`;
}

function shouldSkipDuplicateToast(signature) {
  const now = Date.now();
  if (
    signature === lastToastSignature &&
    now - lastToastTimestamp < TOAST_DEDUPE_MS
  ) {
    return true;
  }
  lastToastSignature = signature;
  lastToastTimestamp = now;
  return false;
}

/**
 * Only force-clear the overlay when no tracked requests remain.
 * Resetting while other calls are in-flight left the counter out of sync
 * and could keep the modal stuck after data already rendered.
 */
function hideGlobalLoaderSafely() {
  try {
    const loader = window.document?.getElementById("loaderoverlay");
    const isVisible =
      loader &&
      (loader.style.display === "flex" ||
        loader.classList.contains("justo-global-loader--visible"));
    if (isVisible) {
      resetGlobalLoader();
    }
  } catch (_error) {
    resetGlobalLoader();
  }
}

function normalizeMessage(msg) {
  if (msg === null || msg === undefined || msg === "") {
    return "Something went wrong. Please try again.";
  }
  if (typeof msg === "object") {
    return msg.message || JSON.stringify(msg);
  }
  return String(msg);
}

const TOAST_LABELS = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Info",
};

function ToastContent({
  type,
  title,
  message,
  iconClass,
  closeToast,
  actionLabel,
  onAction,
}) {
  const icon = iconClass || TOAST_ICONS[type];
  const nextTitle = String(title || "").trim();
  const nextMessage = String(message || "").trim();
  const isGenericTitle = GENERIC_TITLES.has(nextTitle);

  let headline = nextTitle;
  let body = nextMessage;

  if (!nextTitle && nextMessage) {
    headline = nextMessage;
    body = "";
  } else if (isGenericTitle && nextMessage) {
    // Generic label stays as type cue via icon/eyebrow; message is the headline.
    headline = nextMessage;
    body = "";
  } else if (
    nextTitle &&
    nextMessage &&
    nextMessage.toLowerCase() === nextTitle.toLowerCase()
  ) {
    body = "";
  }

  const handleAction = (e) => {
    e.stopPropagation();
    if (typeof onAction === "function") {
      onAction();
    }
    if (typeof closeToast === "function") {
      closeToast();
    }
  };

  return (
    <div
      className={`justo-toast__card justo-toast__card--${type}`}
      role="status"
      aria-live={type === "error" ? "assertive" : "polite"}
    >
      <span
        className={`justo-toast__icon justo-toast__icon--${type}`}
        aria-hidden="true"
      >
        <i className={`fa ${icon}`} />
      </span>

      <div className="justo-toast__copy">
        <span className={`justo-toast__label justo-toast__label--${type}`}>
          {TOAST_LABELS[type] || "Notice"}
        </span>
        <p className="justo-toast__title">{headline}</p>
        {body ? <p className="justo-toast__message">{body}</p> : null}
      </div>

      <div className="justo-toast__actions">
        {actionLabel && typeof onAction === "function" ? (
          <button
            type="button"
            className="justo-toast__action"
            onClick={handleAction}
          >
            {actionLabel}
          </button>
        ) : null}
        {typeof closeToast === "function" ? (
          <button
            type="button"
            className="justo-toast__dismiss"
            aria-label="Dismiss notification"
            onClick={(e) => {
              e.stopPropagation();
              closeToast();
            }}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function showToast(type, msg, options = {}) {
  const message = normalizeMessage(msg);
  const title = options.title || TOAST_TITLES[type];
  const iconClass = options.iconClass;
  const signature = buildToastSignature(type, message, title);

  if (shouldSkipDuplicateToast(signature)) {
    hideGlobalLoaderSafely();
    return;
  }

  const {
    title: _title,
    iconClass: _icon,
    autoClose: autoCloseOpt,
    actionLabel,
    onAction,
    onUndo,
    ...toastOptions
  } = options;

  const resolvedActionLabel =
    actionLabel || (typeof onUndo === "function" ? "Undo" : undefined);
  const resolvedOnAction = onAction || onUndo;

  const autoClose =
    autoCloseOpt === false || autoCloseOpt === 0
      ? false
      : Number(autoCloseOpt) > 0
        ? Number(autoCloseOpt)
        : DEFAULT_AUTO_CLOSE_MS;

  toast(
    ({ closeToast }) => (
      <ToastContent
        type={type}
        title={title}
        message={message}
        iconClass={iconClass}
        closeToast={closeToast}
        actionLabel={resolvedActionLabel}
        onAction={resolvedOnAction}
      />
    ),
    {
      type,
      position: "top-right",
      autoClose,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      pauseOnFocusLoss: false,
      draggable: true,
      draggablePercent: 40,
      closeButton: false,
      icon: false,
      transition: Slide,
      className: `justo-toast justo-toast--${type}`,
      bodyClassName: "justo-toast__body-wrap",
      progressClassName: `justo-toast__progress justo-toast__progress--${type}`,
      ...toastOptions,
      autoClose,
      hideProgressBar: false,
      transition: Slide,
    },
  );

  hideGlobalLoaderSafely();
}

export const successToast = (msg, options) => showToast("success", msg, options);

export const errorToast = (msg, options) => showToast("error", msg, options);

export const warnToast = (msg, options) => showToast("warning", msg, options);

export const infoToast = (msg, options) => showToast("info", msg, options);

function formatPropertyToastMessage(template, propertyTitle) {
  const property = String(propertyTitle || "").trim() || "this property";
  return String(template || "").replace(/\{\{property\}\}/g, property);
}

export function greMobileOtpSettingToast(enabled, propertyTitle) {
  if (enabled) {
    successToast(
      formatPropertyToastMessage(
        strings.greMobileOtpActivatedMessage,
        propertyTitle,
      ),
      {
        title: strings.greMobileOtpActivatedTitle,
        iconClass: "fa-mobile",
      },
    );
    return;
  }

  warnToast(
    formatPropertyToastMessage(
      strings.greMobileOtpDeactivatedMessage,
      propertyTitle,
    ),
    {
      title: strings.greMobileOtpDeactivatedTitle,
      iconClass: "fa-phone",
    },
  );
}
